import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';
import {
  InviteMemberPayload,
  ManageSeatsParamsPayload,
  ManageSeatsResponse,
  MemberDataUpdate,
  SeatKind,
  TeamMembersParams,
  TellentMember,
  TellentMemberResponse,
  TellentMemberRow,
} from '@tellent/types';

import {
  createEntityCollectionStore,
  memoizeSelector,
  MetaScopeOptions,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';

import { resolveMemberSeat, resolveMemberStatus } from '../helpers';
import { EntityStoreKey } from '../types';
import { TellentAppsSelectors } from './tellent-apps.store';

const idSelector = ({ guid }: { guid: string }) => guid;

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<TellentMember>()(
  EntityStoreKey.TellentMembers,
  {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => ({
      inviteTellentMember: (state, payload: InviteMemberPayload) => adapter.markAsPending(state),
      inviteTellentMemberSuccess: (state, payload: TellentMember[]) => adapter.markAsLoaded(state),
      inviteTellentMemberFail: ngrxEvent<HttpErrorResponse>(),

      inviteAppMember: (state, payload: InviteMemberPayload) => adapter.markAsPending(state),
      inviteAppMemberSuccess: (state, payload: TellentMember[]) => adapter.markAsLoaded(state),
      inviteAppMemberFail: ngrxEvent<HttpErrorResponse>(),

      fetchPaginatedCollection: (state, payload: { params: TeamMembersParams }) =>
        adapter.markAsPending(state),

      deactivateMember: (state, payload: { guid: string }) => adapter.markAsPending(state),
      deactivateMemberSuccess: (state, payload: TellentMemberResponse) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload.data),
          next => adapter.markAsLoaded(next),
        ),
      deactivateMemberFailed: ngrxEvent<HttpErrorResponse>(),

      reactivateMember: (state, payload: { guid: string }) => adapter.markAsPending(state),
      reactivateMemberSuccess: (state, payload: TellentMemberResponse) =>
        adapter.markAsLoaded(state),
      reactivateMemberFailed: ngrxEvent<HttpErrorResponse>(),

      resendInvitation: (state, payload: { id: number }) => adapter.markAsPending(state),
      resendInvitationSuccess: (state, payload: { id: number }) => adapter.markAsLoaded(state),
      resendInvitationFailed: ngrxEvent<HttpErrorResponse>(),

      sendInvitation: (state, payload: { guid: string }) => adapter.markAsPending(state),
      sendInvitationSuccess: (state, payload: { guid: string }) => adapter.markAsLoaded(state),
      sendInvitationFailed: ngrxEvent<HttpErrorResponse>(),

      manageSeats: (state, payload: ManageSeatsParamsPayload) => adapter.markAsPending(state),
      manageSeatsSuccess: (state, payload: ManageSeatsResponse) => adapter.markAsLoaded(state),
      manageSeatsFailed: ngrxEvent<HttpErrorResponse>(),

      updateMember: (state, payload: MemberDataUpdate) => adapter.markAsPending(state),
      updateMemberSuccess: (state, payload: TellentMemberResponse) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload.data),
          next => adapter.markAsLoaded(next),
        ),
      updateMemberFail: ngrxEvent<HttpErrorResponse>(),

      sendPasswordReset: (state, payload: { guid: string }) => adapter.markAsPending(state),
      sendPasswordResetSuccess: state => adapter.markAsLoaded(state),
      sendPasswordResetFail: ngrxEvent<HttpErrorResponse>(),
    }),
    idSelector,
  },
);

const teamMembersRowSelector = memoizeSelector((props: MetaScopeOptions) =>
  createSelector(
    selectors.entitiesWithPagination({ scope: props.scope }),
    TellentAppsSelectors.entities(),
    ({ rows }, apps): TellentMemberRow[] =>
      rows.map(member => ({
        ...member,
        id: member.guid, // id is required for the data-table
        status: resolveMemberStatus(member),
        recruitment: resolveMemberSeat(member, apps, SeatKind.Recruitment),
        coreHr: resolveMemberSeat(member, apps, SeatKind.CoreHR),
      })),
  ),
);

const teamMemberById = memoizeSelector((memberGuid: string) =>
  createSelector(
    selectors.byId(memberGuid),
    (member): TellentMember => ({
      ...member!,
      status: resolveMemberStatus(member!),
      owner: !!member?.owner,
    }),
  ),
);

const TellentMembersSelectors = {
  ...selectors,
  teamMembersRowSelector,
  teamMemberById,
};

export {
  actions as TellentMembersActions,
  TellentMembersSelectors,
  reducer as TellentMembersReducer,
  namespace as TellentMembersNamespace,
};
